import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";

const EVAL_BLOCKS = [
  {
    icon: "📝",
    title: "اختبارات الوحدات",
    desc: "10 أسئلة لكلّ وحدة بمعدّل نجاح 60% — تَضمن ثَبات المفاهيم قبل الانتقال.",
  },
  {
    icon: "🎓",
    title: "الاختبارات النهائيّة",
    desc: "15 سؤالًا شاملًا لكلّ مسار بمعدّل نجاح 70% — بَوّابة الشهادة.",
  },
  {
    icon: "🤖",
    title: "تقييم الذكاء الاصطناعيّ",
    desc: "كلّ مهمّة كتابةٍ تُقَيَّم بـAI على معايير المسار (4–7 معايير) ويُعطي نَصائح فوريّة.",
  },
  {
    icon: "🏅",
    title: "شهادة PDF مستقلّة",
    desc: "شهادةٌ مُختَزَنةٌ في الخادم بِرقم تَحقّقٍ فريد + تواريخ هجريّة وميلاديّة + خانة QR.",
  },
];

const LEARNING_PILLARS = [
  { i: "🌳", t: "التدرّج البنائيّ", d: "من المفهوم البسيط إلى التطبيق المركّب عبر 10 مراحل." },
  { i: "📖", t: "أصالة المصادر", d: "أمثلة من القرآن والحديث وعيون الشعر العربيّ." },
  { i: "🧠", t: "التفاعل الذكيّ", d: "محادثاتٌ مع معلّم AI + تقييماتٌ مكتوبة." },
  { i: "🏆", t: "إنجازٌ مَحسوس", d: "XP وشارات وشهادات بعد كلّ مَرحلة." },
];

export default function MethodologyPage() {
  const navigate = useNavigate();
  const stats = useStats();
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    api.listTracks().then((d) => setTracks(d.tracks || [])).catch(() => {});
  }, []);

  const platformStats = [
    { value: String(stats.tracks), label: "مسارات تعليميّة" },
    { value: String(stats.lessons), label: "درسًا تفاعليًّا" },
    { value: String(stats.tests), label: "اختبارًا (وحدات ونهائيّة)" },
    { value: String(stats.certificates), label: "شهادات PDF" },
    { value: String(stats.stages_per_lesson), label: "مراحل لكلّ درس" },
  ];

  return (
    <div className="shell has-bottom-nav" data-testid="methodology-page">
      {/* ===== HERO ===== */}
      <div className="page-header methodology-hero" data-testid="methodology-hero">
        <div className="hero-emblem">📚</div>
        <h1>
          المنهج <span className="gold">التعليميّ</span>
        </h1>
        <p className="hero-sub">
          خَريطةٌ كاملة لطريقة بَلاغة في تعليم البلاغة العربيّة — من المفهوم
          الأوّل حتّى المشروع الختاميّ.
        </p>
      </div>

      {/* ===== PROJECT BRIEF ===== */}
      <section className="meth-card" data-testid="meth-brief">
        <h2>📜 تَعريفٌ مُختَصَر بالمشروع</h2>
        <p>
          منصّةُ <strong>بَلاغة</strong> هي مَنصّةٌ عربيّةٌ ذكيّة تَجمع عَراقة
          علم البلاغة بأدوات الذكاء الاصطناعيّ الحديثة. تَهدف إلى تَمكين كلّ
          مُتعلّمٍ عربيّ — تلميذًا كان أم كاتبًا — من إتقان أساليب البلاغة
          وتذوّقها، عبر منهجٍ بنائيٍّ تَدرّجيٍّ يَنطلق من المبتدئ إلى الاحترافيّ.
        </p>
      </section>

      {/* ===== TOP STATS ===== */}
      <section className="meth-stats-row" data-testid="meth-stats">
        {platformStats.map((s) => (
          <div key={s.label} className="meth-stat">
            <div className="v gold">{s.value}</div>
            <div className="l">{s.label}</div>
          </div>
        ))}
      </section>

      {/* ===== TRACKS TABLE ===== */}
      <section className="meth-card" data-testid="meth-tracks-list">
        <h2>🗺️ المسارات الحاليّة ومستوياتها</h2>
        <div className="meth-tracks-table">
          <div className="meth-tracks-head">
            <span>الرمز</span>
            <span>المسار</span>
            <span>المستوى</span>
            <span>الدروس</span>
            <span>الشهادة</span>
          </div>
          {tracks.length === 0 && (
            <div className="meth-empty">جارٍ تحميل المسارات…</div>
          )}
          {tracks.map((t) => (
            <div
              key={t.id}
              className="meth-tracks-row"
              data-testid={`meth-track-row-${t.id}`}
              onClick={() => t.status === "available" && navigate("/tracks")}
            >
              <span className="code-pill" style={{ color: t.accent || "var(--gold)" }}>
                {t.icon} {t.code}
              </span>
              <span className="meth-tname">
                <strong>{t.title}</strong>
                <em>{t.subtitle}</em>
              </span>
              <span className="meth-level">{t.level_label || "—"}</span>
              <span className="meth-count">
                {t.stats?.total_lessons || 0}
              </span>
              <span className="meth-cert">
                {t.final_test_id ? "✅" : "—"}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== LEARNING PILLARS ===== */}
      <section className="meth-card" data-testid="meth-pillars">
        <h2>🌟 أركان المنهج</h2>
        <div className="meth-pillars">
          {LEARNING_PILLARS.map((p) => (
            <div key={p.t} className="meth-pillar">
              <div className="meth-pillar-icon">{p.i}</div>
              <h4>{p.t}</h4>
              <p>{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 10 STAGES ===== */}
      <section className="meth-card" data-testid="meth-stages">
        <h2>🔟 المراحل العشر لكلّ درس</h2>
        <ol className="meth-stages">
          <li><span>1</span> المقدّمة وتأطير السياق</li>
          <li><span>2</span> المفردات وتأسيس المعجم البلاغيّ</li>
          <li><span>3</span> الشرح المُفصَّل مع تَحقّقات وسطيّة</li>
          <li><span>4</span> الأمثلة من القرآن والشعر</li>
          <li><span>5</span> الأنشطة التفاعليّة (اختيار/مُطابقة/إكمال)</li>
          <li><span>6</span> التمرين الكتابيّ المُحرَّر</li>
          <li><span>7</span> محادثة معلّم الذكاء الاصطناعيّ</li>
          <li><span>8</span> مهمّة الكتابة الحرّة + تقييم AI</li>
          <li><span>9</span> الكويز النهائيّ للدرس</li>
          <li><span>10</span> الملخّص + بطاقة المراجعة + الشارة</li>
        </ol>
      </section>

      {/* ===== LEARNING OUTCOMES ===== */}
      <section className="meth-card" data-testid="meth-outcomes">
        <h2>🎯 نَواتج التَعلّم العامّة</h2>
        <ul className="meth-outcomes">
          <li>إدراكُ علوم البلاغة الثلاثة (المعاني، البيان، البديع) وتَطبيقاتها.</li>
          <li>إتقانُ التَشبيه والاستعارة والكناية والمحسّنات البديعيّة.</li>
          <li>تَوظيفُ أساليب التأثير العاطفيّ (الباثوس) واللوغوس والإيتوس في الإقناع.</li>
          <li>كتابةُ الفقرة البلاغيّة، الخطبة، والمقال البليغ.</li>
          <li>تَحليلُ النصوص القرآنيّة والشعريّة بَلاغيًّا.</li>
          <li>إنجازُ مَشروعَين بَلاغيَّين تَتويجيَّين يُقَيَّمان بمعايير احترافيّة.</li>
          <li>التَخرّجُ بسبع شهاداتٍ مستقلّة موثّقة بِرقم تَحقّقٍ فريد.</li>
        </ul>
      </section>

      {/* ===== EVALUATION & CERTIFICATES ===== */}
      <section className="meth-card" data-testid="meth-eval">
        <h2>📊 آليّة التَقييم والشهادات</h2>
        <div className="meth-eval-grid">
          {EVAL_BLOCKS.map((b) => (
            <div key={b.title} className="meth-eval-block">
              <div className="meth-eval-icon">{b.icon}</div>
              <h4>{b.title}</h4>
              <p>{b.desc}</p>
            </div>
          ))}
        </div>
        <div className="meth-cert-note">
          <strong>كلّ شهادة تَحمل:</strong> رقم تَحقّقٍ فريد بصيغة
          <code className="badge-inline">BAL-XXXX-XXXX-XXXX</code>،
          وتاريخ إصدارٍ ميلاديٍّ وهجريّ، واسم المسار بالعربيّة والإنجليزيّة،
          وخانةٌ مُجَهَّزة لرَمز QR للتحقّق المستقبليّ.
        </div>
      </section>

      {/* ===== CTA ===== */}
      <div className="meth-cta">
        <button
          className="btn btn-gold"
          onClick={() => navigate("/tracks")}
          data-testid="meth-cta-tracks"
        >
          استكشف خَريطة المسارات →
        </button>
      </div>
    </div>
  );
}
